/**
 * Web Push
 *
 * Registers this browser as an FCM recipient so notifications arrive even when
 * the tab is closed. Reuses the backend's existing FCM rail (`device_tokens` +
 * `pushService`) by registering with `platform: 'web'` — no second VAPID stack.
 *
 * Everything here degrades to a no-op when the Firebase env vars are absent
 * (local dev) or the browser lacks support, so callers can invoke it
 * unconditionally and read `getState()` to decide what to render.
 *
 * The permission prompt is NEVER triggered on load — only from an explicit
 * user action. A `denied` result is sticky and cannot be undone from script,
 * so an unsolicited prompt permanently costs the user the feature.
 */

import { api } from '../api/client';

/**
 * Outcome of an enable attempt.
 *
 * `state` and `registered` are SEPARATE on purpose. The first version returned
 * only a PushState and reported `'default'` whenever token registration failed —
 * even though permission had in fact been granted. So the UI claimed "never
 * asked" while the browser said "allowed": the enable prompt stayed on screen
 * until a reload read the real permission, and the underlying failure vanished
 * into console.error where nobody would see it. (Observed in production: the
 * device_tokens table was missing for that tenant, so the POST 500'd.)
 */
export interface PushRegistration {
  /** Derived from the real permission. Never masks `granted` as `default`. */
  state: PushState;
  /** A token was obtained AND accepted by the backend. */
  registered: boolean;
  /** Set when permission was granted but registration could not complete. */
  error?: string;
}

export type PushState =
  | 'unsupported'      // browser lacks Notification/ServiceWorker/Push
  | 'not-configured'   // no Firebase env config in this deployment
  | 'default'          // supported and configured, not yet asked
  | 'granted'
  | 'denied';

interface FirebaseConfig {
  apiKey: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

const readConfig = (): FirebaseConfig | null => {
  const env = import.meta.env;
  const cfg = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    vapidKey: env.VITE_FIREBASE_VAPID_KEY,
  };
  if (Object.values(cfg).some((v) => !v)) return null;
  return cfg as FirebaseConfig;
};

const isSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

export const getState = (): PushState => {
  if (!isSupported()) return 'unsupported';
  if (!readConfig()) return 'not-configured';
  const permission = Notification.permission;
  if (permission === 'granted') return 'granted';
  if (permission === 'denied') return 'denied';
  return 'default';
};

/** Token this browser last registered, so logout can deregister precisely. */
const TOKEN_STORAGE_KEY = 'eventik.pushToken';

const readStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private mode / blocked storage — registration still works this session.
  }
};

/**
 * Lazily pull in the Firebase SDK. Kept dynamic so the ~100KB messaging
 * bundle never lands in the initial page load for users who don't use push.
 */
const loadMessaging = async (config: FirebaseConfig) => {
  const [{ initializeApp, getApps }, messaging] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);

  const app = getApps().length
    ? getApps()[0]!
    : initializeApp({
        apiKey: config.apiKey,
        projectId: config.projectId,
        messagingSenderId: config.messagingSenderId,
        appId: config.appId,
      });

  return { messaging, instance: messaging.getMessaging(app) };
};

/**
 * Ask for permission (if not already granted) and register this device.
 * Returns the resulting state. Safe to call repeatedly — re-registering an
 * existing token is an upsert on the backend.
 */
export const requestAndRegister = async (): Promise<PushRegistration> => {
  const config = readConfig();
  if (!isSupported()) return { state: 'unsupported', registered: false };
  if (!config) return { state: 'not-configured', registered: false };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { state: permission === 'denied' ? 'denied' : 'default', registered: false };
  }

  // Past this point permission IS granted, so every return path says so. A
  // registration failure is reported via `registered`/`error` — never by
  // pretending the user was never asked.
  try {
    // Config goes in the query string, read synchronously by the worker at top
    // level. Fetching it inside the worker loses cold-start pushes: the SW is
    // terminated when idle, so a push typically arrives before an awaited fetch
    // resolves and before the FCM SDK's push listener is installed. The query
    // string does not affect SW scope; changing it triggers a normal SW update.
    const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams({
      apiKey: config.apiKey,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    }).toString()}`;

    const registration = await navigator.serviceWorker.register(swUrl);
    const { messaging, instance } = await loadMessaging(config);

    const token = await messaging.getToken(instance, {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!token) return { state: 'granted', registered: false, error: 'no-token' };

    await api.notifications.registerDeviceToken(token, 'web');
    storeToken(token);
    return { state: 'granted', registered: true };
  } catch (err) {
    console.error('Web push registration failed:', err);
    return {
      state: 'granted',
      registered: false,
      error: err instanceof Error ? err.message : 'registration-failed',
    };
  }
};

/**
 * Deregister this browser's token. Called on logout so a shared computer stops
 * receiving the previous user's notifications.
 *
 * `authToken` must be captured by the caller BEFORE it clears credentials.
 * Axios request interceptors run on a microtask, so by the time the interceptor
 * reads the token out of localStorage, a synchronous `clearUserInfo()` on the
 * next line has already removed it — the request would go out unauthenticated,
 * be rejected by `protect`, and leave the device token in place. Passing it
 * explicitly wins, because the interceptor never overwrites a caller-set
 * Authorization header.
 */
export const unregister = async (authToken?: string | null): Promise<void> => {
  const token = readStoredToken();
  if (!token) return;

  // Clear the local record immediately: whatever happens to the request, this
  // browser must stop claiming the token belongs to the departing session.
  storeToken(null);

  try {
    await api.notifications.removeDeviceToken(token, authToken ?? undefined);
  } catch {
    // Best effort — the backend prunes stale tokens on send failure anyway.
  }
};

/**
 * Re-register on app start when permission is already granted, so a rotated
 * FCM token does not silently stop delivery. No-op otherwise, and never
 * prompts.
 */
export const refreshIfGranted = async (): Promise<void> => {
  if (getState() !== 'granted') return;
  await requestAndRegister();
};
